// Shared Web Audio unlock for the fan Watch (LSC-638).
//
// The fan Room mixes every remote track through ONE Web Audio graph (webAudioMix,
// LSC-598). On iOS (Safari + every iOS browser, all WebKit) an AudioContext only
// starts `running` when it is created/resumed INSIDE a user gesture. LiveKit creates
// its context at connect time — after the async watch-token fetch, long past the tap
// that opened Watch — so it starts `suspended` and the Watch is silent until the next
// tap on the page (LiveKit's own resume listener). That's why "entering fullscreen
// starts audio": the Full-screen tap was simply the first tap after connect.
//
// Fix: the app owns ONE long-lived AudioContext and hands it to the Room
// (`webAudioMix: { audioContext }` — a provided context is never closed by LiveKit).
// A capture-phase listener installed at boot creates/resumes it inside the FIRST tap
// anywhere in the app, so by the time a fan navigates home → Cave → Watch it is
// already running and the Watch opens audible. A cold load/refresh straight into
// Watch has had no gesture yet; there the Watch shows its Enable-audio control and any
// tap (on it or anywhere) resumes the same context.
//
// navigator.audioSession (iOS 17+) is set to "playback" while a Watch is mounted so
// Web Audio behaves like media playback (not silenced by the ring/silent switch), and
// restored to "auto" when the Watch unmounts so the rest of the app doesn't interrupt
// a fan's background music.

type AudioContextCtor = typeof AudioContext;

let ctx: AudioContext | null = null;
let installed = false;

function ctor(): AudioContextCtor | undefined {
  if (typeof window === "undefined") return undefined;
  const w = window as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
  return w.AudioContext ?? w.webkitAudioContext;
}

/** The app-wide AudioContext, created on demand. `undefined` where Web Audio is unavailable (jsdom). */
export function getSharedAudioContext(): AudioContext | undefined {
  if (ctx && ctx.state !== "closed") return ctx;
  const C = ctor();
  if (!C) return undefined;
  try {
    ctx = new C({ latencyHint: "interactive" });
  } catch {
    return undefined;
  }
  return ctx;
}

/**
 * Create/resume the shared context. Call SYNCHRONOUSLY from a user-gesture handler
 * (click/touchend/keydown) — iOS only honours resume() inside the gesture.
 */
export function unlockAudio(): void {
  const c = getSharedAudioContext();
  if (!c || c.state === "running") return;
  // resume() can stay pending until a gesture on iOS; never let it surface as unhandled.
  c.resume().catch(() => undefined);
}

/** Install the boot-time first-gesture unlock (idempotent). Listeners stay — iOS can
 *  re-suspend/interrupt the context (a phone call, backgrounding) and the next tap
 *  should bring it back. Once running, each event is a cheap state check. */
export function installAudioUnlock(): void {
  if (installed || typeof document === "undefined" || !ctor()) return;
  installed = true;
  const onGesture = () => unlockAudio();
  for (const ev of ["click", "touchend", "keydown"]) {
    document.addEventListener(ev, onGesture, { capture: true, passive: true });
  }
}

type AudioSessionType = "auto" | "playback" | "transient" | "transient-solo" | "ambient" | "play-and-record";

/** Set navigator.audioSession.type where supported (iOS/Safari 17+); a no-op elsewhere. */
export function setAudioSessionType(type: AudioSessionType): void {
  if (typeof navigator === "undefined") return;
  const s = (navigator as unknown as { audioSession?: { type: AudioSessionType } }).audioSession;
  if (!s) return;
  try {
    s.type = type;
  } catch {
    // unsupported value / locked session — best-effort
  }
}

/** Test-only: drop the module state. */
export function __resetAudioUnlockForTests(): void {
  ctx = null;
  installed = false;
}
